import React from "react";
import styled from "styled-components";
import { Page } from "../components/PageLayout";
import { BackstageState } from "../redux/store";
import ConnectStripeButton from "../components/ConnectStripeButton";
import GET_PROFILE from "@sellout/models/.dist/graphql/queries/profile.query";
import DISCONNECT_STRIPE from "@sellout/models/.dist/graphql/mutations/resetStripeConnection.mutation";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { Colors, Loader, LoaderSizes } from "@sellout/ui";
import Error from "../elements/Error";
import Button, { ButtonTypes } from "@sellout/ui/build/components/Button";
import TextButton from "@sellout/ui/build/components/TextButton";
import * as Intercom from "../utils/Intercom";
import * as AppActions from "../redux/actions/app.actions";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "@sellout/ui/build/utils/ErrorUtil";
import IS_SUPER_USER from "@sellout/models/.dist/graphql/queries/isSuperUser.query";

const Title = styled.div`
  color: ${Colors.Grey1};
  font-weight: 600;
  font-size: 2.4rem;
`;

const Subtitle = styled.div`
  color: ${Colors.Grey2};
  font-weight: 500;
  font-size: 1.4rem;
  margin: 10px 0px 20px;
  display: flex;
  align-items: center;
  flex-flow: wrap;
  gap: 10px;
`;

const StripeIdStyle = styled.div`
  color: ${Colors.Grey2};
  font-size: 2.4rem;
  margin: 0px 20px 0px 10px;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-flow: wrap;
  gap: 10px;
`;

const LoadingText = styled.span`
  color: ${Colors.Grey3};
`;

export enum SettingsPayoutsTextEnum {
  ConnectStripeTitle = "Please connect your Stripe account to publish events and receive payment",
  ConnectStripeSubtitle = "Click the button below to connect your account. Stripe is the trusted payment provider for Sellout.",
  StripeConnectedTitle = "Stripe account connected:",
  OpenStripeDashboard = "OPEN YOUR STRIPE DASHBOARD",
  ChangeStripeSubtitle = "To change your stripe account, please",
  SpeakWithTeam = "to speak with our customer success team.",
  DisconnectStripeTitle = "Disconnect Stripe Account",
  DisconnectStripeMessage = "Are you sure you want to disconnect your Stripe account? This will prevent you from processing payments until you reconnect.",
  DisconnectButton = "DISCONNECT",
  CancelButton = "CANCEL",
  DisconnectSuccess = "Stripe account disconnected successfully",
  DisconnectError = "Failed to disconnect Stripe: ",
  ResetStripeConnection = "to reset this organization's Stripe connection",
  ClickHere = "Click Here",
}

type SettingsPayoutsProps = {};
const SettingsPayouts: React.FC<SettingsPayoutsProps> = () => {

  const { orgId } = useSelector((state: BackstageState) => state.app);
  const [errorMsg, setErrorMsg] = React.useState("");
  const dispatch = useDispatch();


  const { data: isUserRole } = useQuery(IS_SUPER_USER);

  const { data, loading, refetch } = useQuery(GET_PROFILE);
  const [disconnectStripe, { loading: disconnectLoading }] = useMutation(
    DISCONNECT_STRIPE,
    {
      onCompleted() {
        dispatch(
          AppActions.showNotification(
            SettingsPayoutsTextEnum.DisconnectSuccess,
            AppNotificationTypeEnum.Success
          )
        );
        window.history.replaceState({}, document.title, '/admin/dashboard/settings/payouts');
        refetch();
      },
      onError(error) {
        const errorMessage = getErrorMessage(error);
        setErrorMsg(errorMessage);
        dispatch(
          AppActions.showNotification(
            `${SettingsPayoutsTextEnum.DisconnectError}${errorMessage}`,
            AppNotificationTypeEnum.Error
          )
        );
      },
    }
  );

  const handleDisconnect = () => {
    dispatch(
      AppActions.pushModalConfirmAction({
        title: SettingsPayoutsTextEnum.DisconnectStripeTitle,
        message: SettingsPayoutsTextEnum.DisconnectStripeMessage,
        confirm: () => {
          disconnectStripe({variables: { orgId } });
          dispatch(AppActions.popModal());
        },
        confirmText: SettingsPayoutsTextEnum.DisconnectButton,
        cancel: () => {
          dispatch(AppActions.popModal());
        },
        cancelText: SettingsPayoutsTextEnum.CancelButton,
      })
    );
  };

  const {
    platformSettings: { stripeClientId },
    organization: { stripeId },
  } = data;
  const stripeRedirectUrl = `${window.location.origin}/admin/dashboard/settings/payouts`;
  const showConnectStripe = Boolean(
    data?.organization && !data?.organization.stripeId
  );
  return (
    <Page>
      {showConnectStripe ? (
        <>
          <Title>
            {SettingsPayoutsTextEnum.ConnectStripeTitle}
          </Title>
          <Subtitle>
            {SettingsPayoutsTextEnum.ConnectStripeSubtitle}
          </Subtitle>
          <ConnectStripeButton
            stripeClientId={stripeClientId}
            stripeRedirectUrl={stripeRedirectUrl}
            stripeId={stripeId}
            setErrorMsg={setErrorMsg}
          />
        </>
      ) : (
        <>
          <RowWrapper>
            <Title>{SettingsPayoutsTextEnum.StripeConnectedTitle}</Title>
            <StripeIdStyle>{data?.organization?.stripeId}</StripeIdStyle>
          </RowWrapper>
          <Button
            type={ButtonTypes.Regular}
            text={SettingsPayoutsTextEnum.OpenStripeDashboard}
            loading={loading}
            margin="20px 0px 15px 0px"
            onClick={() =>
              window.open("https://dashboard.stripe.com", "_blank")
            }
          />
           <Subtitle>
            {SettingsPayoutsTextEnum.ChangeStripeSubtitle} &nbsp;
            <TextButton onClick={() => Intercom.toggle()}>
              {SettingsPayoutsTextEnum.ClickHere}
            </TextButton>
            &nbsp; {SettingsPayoutsTextEnum.SpeakWithTeam}
          </Subtitle>

          {isUserRole?.isSuperUser && (
            <Subtitle>
              {disconnectLoading ? (
                <LoadingText>{SettingsPayoutsTextEnum.ClickHere}</LoadingText>
              ) : (
                <TextButton onClick={handleDisconnect}>
                  {SettingsPayoutsTextEnum.ClickHere}
                </TextButton>
              )}
              &nbsp;{SettingsPayoutsTextEnum.ResetStripeConnection}
              {disconnectLoading && (
                <>&nbsp;<Loader size={LoaderSizes.Small} color={Colors.Orange} /></>
              )}
            </Subtitle>
          )}
        </>
      )}
      <Error margin="10px 0px 0px 0px">{errorMsg}</Error>
    </Page>
  );
};

export default SettingsPayouts;
